import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Receipt, DollarSign, Clock, Ban, RefreshCw, Medal } from "lucide-react";
import { formatDOP } from "@/lib/format";

/**
 * Widget de leaderboard + métricas personales del cajero.
 * Escalable: agrupa por cashier_id vía aggregation en el backend.
 */
export default function CashierLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/cashiers");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh cada 30s
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;
  const me = data.me || {};
  const board = data.leaderboard || [];

  const rankColor = (r) => {
    if (r === 1) return "text-yellow-500";
    if (r === 2) return "text-slate-400";
    if (r === 3) return "text-amber-700";
    return "text-muted-foreground";
  };

  return (
    <div className="mb-4 space-y-3" data-testid="cashier-leaderboard">
      {/* Mis stats destacadas */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-primary" />
            <h3 className="font-bold text-sm">Mi productividad de hoy</h3>
            {me.rank && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${rankColor(me.rank)} bg-background/50`} data-testid="my-rank-badge">
                {me.rank === 1 ? "🥇" : me.rank === 2 ? "🥈" : me.rank === 3 ? "🥉" : `#${me.rank}`}
              </span>
            )}
          </div>
          <button onClick={load} className="text-xs text-muted-foreground hover:text-foreground" data-testid="leaderboard-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MetricCell icon={Receipt} label="Facturas" value={me.invoices_count || 0} testid="me-invoices" />
          <MetricCell icon={DollarSign} label="Total cobrado" value={formatDOP(me.total || 0)} testid="me-total" highlight />
          <MetricCell icon={Trophy} label="Ticket promedio" value={formatDOP(me.avg_ticket || 0)} testid="me-avg-ticket" />
          <MetricCell icon={Clock} label="Tiempo prom." value={me.avg_time_display || "—"} testid="me-avg-time" />
          <MetricCell icon={Ban} label="Canceladas" value={data.cancelled_by_me || 0} testid="me-cancelled" />
        </div>
      </div>

      {/* Leaderboard general */}
      {board.length > 1 && (
        <details className="bg-card border rounded-md" data-testid="leaderboard-panel">
          <summary className="p-3 cursor-pointer flex items-center justify-between hover:bg-accent/30">
            <div className="flex items-center gap-2">
              <Medal className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Ranking de cajeros del día</span>
              <span className="text-xs text-muted-foreground">({board.length})</span>
            </div>
            <span className="text-xs text-muted-foreground">Ver clasificación</span>
          </summary>
          <div className="border-t overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="p-2 text-left overline">#</th>
                  <th className="p-2 text-left overline">Cajero</th>
                  <th className="p-2 text-right overline">Facturas</th>
                  <th className="p-2 text-right overline">Total</th>
                  <th className="p-2 text-right overline">Ticket prom.</th>
                  <th className="p-2 text-right overline">Tiempo prom.</th>
                </tr>
              </thead>
              <tbody>
                {board.map((c) => (
                  <tr
                    key={c.cashier_id}
                    className={`border-t ${c.is_me ? "bg-primary/5 font-semibold" : "hover:bg-accent/20"}`}
                    data-testid={`leaderboard-row-${c.rank}`}
                  >
                    <td className={`p-2 font-black ${rankColor(c.rank)}`}>
                      {c.rank === 1 ? "🥇" : c.rank === 2 ? "🥈" : c.rank === 3 ? "🥉" : `#${c.rank}`}
                    </td>
                    <td className="p-2">
                      {c.cashier_name}
                      {c.is_me && <span className="ml-2 text-[10px] uppercase text-primary">Tú</span>}
                    </td>
                    <td className="p-2 text-right">{c.invoices_count}</td>
                    <td className="p-2 text-right data-cell font-bold">{formatDOP(c.total)}</td>
                    <td className="p-2 text-right data-cell">{formatDOP(c.avg_ticket)}</td>
                    <td className="p-2 text-right">{c.avg_time_display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

function MetricCell({ icon: Icon, label, value, testid, highlight }) {
  return (
    <div className="bg-background/60 border rounded p-2" data-testid={testid}>
      <div className="flex items-center gap-1 mb-1">
        <Icon className="w-3 h-3 text-muted-foreground" />
        <span className="overline text-[10px]">{label}</span>
      </div>
      <div className={`font-black ${highlight ? "text-lg text-primary" : "text-base"}`}>{value}</div>
    </div>
  );
}
